/**
 * El Nº de Trámite impreso, ubicado con certeza.
 *
 * El código de barras del frente 2012 lleva el número adentro, así que se sabe
 * QUÉ número buscar: el OCR de su banda solo tiene que decir DÓNDE está. Medido
 * sobre 16 frentes reales, la banda leída sola, ampliada y con solo dígitos, lo
 * encuentra en 13; la plantilla queda para los otros tres.
 */
import { PalabraOcr } from "../dniMatch";
import { Rectangulo } from "./types";

const digitos = (t: string) => String(t ?? "").replace(/\D/g, "");

/** La palabra del OCR que es este trámite: el número entero, o nueve dígitos seguidos de su principio o su final. */
export function palabraDelTramite(palabras: PalabraOcr[], tramite: string): PalabraOcr | null {
  const t = digitos(tramite);
  if (t.length < 9) return null;
  return (
    palabras.find((w) => {
      const d = digitos(w.text);
      // Como mucho un dígito de ruido de más: con más, el OCR pegó otro número (el
      // documento, por ejemplo) y su caja lo taparía también.
      return (
        d.length >= 9 &&
        d.length <= t.length + 1 &&
        (t.includes(d) || d.includes(t.slice(0, 9)) || d.includes(t.slice(-9)))
      );
    }) ?? null
  );
}

/** La caja, dentro de la banda donde se buscó: lo que se tapa no sale de ahí. */
export function recortarA(caja: Rectangulo, banda: Rectangulo): Rectangulo {
  const x = Math.max(caja.x, banda.x);
  const y = Math.max(caja.y, banda.y);
  const x1 = Math.min(caja.x + caja.w, banda.x + banda.w);
  const y1 = Math.min(caja.y + caja.h, banda.y + banda.h);
  return { x, y, w: Math.max(0, x1 - x), h: Math.max(0, y1 - y) };
}

/**
 * La caja del número entero a partir de la palabra encontrada: si el OCR se comió
 * dígitos, se estira por los que faltan, a los dos lados (no se sabe de cuál), y
 * se le da un margen de medio renglón.
 */
export function cajaDelTramiteEntero(w: PalabraOcr, tramite: string): Rectangulo {
  const leidos = Math.max(1, digitos(w.text).length);
  const faltan = Math.max(0, digitos(tramite).length - leidos);
  const ancho = w.bbox.x1 - w.bbox.x0;
  const alto = w.bbox.y1 - w.bbox.y0;
  const porDigito = ancho / leidos;
  const margen = alto * 0.5;
  const extra = faltan * porDigito + margen;
  return { x: w.bbox.x0 - extra, y: w.bbox.y0 - margen, w: ancho + 2 * extra, h: alto + 2 * margen };
}
